import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomButton from "../../../../customs/Button";
import {styled} from '@mui/material'
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { useMutation , useQueryClient} from "@tanstack/react-query";
import * as Yup from 'yup';
import { useContext, useEffect } from "react";
import { NotificationContext } from "../../../../providers/notification";

interface CreatePaymentProps {
  state?: 'create' | 'update',
  data?: PaymentDurationDetails,
  cancel: React.Dispatch<React.SetStateAction<boolean>>
}

interface PaymentDurationDetails {
  Id: number;
  PaymentType: string;
  StartDate: string;
  EndDate: string;
  IsActive: boolean;
}

function convertDate(dateString: string){
  if(!dateString) return;
  let p = dateString?.split(/\D/g)
  return [p[0], p[1], p[2]].join("-");
}

const CreatePaymentDuration = ({state = 'create', data, cancel }: CreatePaymentProps)  => {

  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const validationSchema = Yup.object().shape({
    startDate: Yup.date().min(new Date(), 'Start Date cannot be in the past').required('required'),     
    endDate: Yup.date().min(Yup.ref('startDate'), 'End date cannot be before start date').required('required'),
    status: Yup.boolean().required('required'),
    paymentType: Yup.string().required('required'),
  });

  const handleCancel = () => {
    cancel(false);
  }

  //create
  const  postPaymentDuration = async (data: FormData) => {
    return ( await apiCallFormData().post(`/SetUp/CreateUpdatePaymentDuration`, data ))
  }

  const postPaymentDurationMutation = useMutation(['post-payment-duration'], postPaymentDuration);

  const handlePostPaymentDuration = async (values: FormikValues) => {

    const payload = new FormData();
    payload.append('StartDate', values?.startDate);
    payload.append('EndDate', values?.endDate);
    payload.append('PaymentType', values?.paymentType);
    payload.append('IsActive', values?.status);

    try{
      postPaymentDurationMutation.mutateAsync(payload, {
        onSuccess: () => {
          showNotification({
            message: 'Successful',
            type: 'success'
          });
          queryClient.invalidateQueries(['get-payment-duration']);
          handleCancel();
          }
        });
    }catch(error: any){
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: 'error'
      });
    }
  }
  
  //Update
  useEffect(() => {
    if(state === 'update'){
      formik.setFieldValue('startDate', convertDate(data?.StartDate || ''))
      formik.setFieldValue('endDate', convertDate(data?.EndDate || ''))
      formik.setFieldValue('status', data?.IsActive)
      formik.setFieldValue('paymentType', data?.PaymentType)
    }
  }, [data])

  const handleEditPaymentDuration = async (values: FormikValues) => {
    const payload = new FormData();
    payload.append('Id', `${data?.Id}`);
    payload.append('StartDate', values?.startDate);
    payload.append('EndDate', values?.endDate);
    payload.append('PaymentType', values?.paymentType);
    payload.append('IsActive', values?.status);

    try{
      postPaymentDurationMutation.mutateAsync(payload, {
        onSuccess: () => {
          showNotification({
            message: 'Successful',
            type: 'success'
          });
          queryClient.invalidateQueries(['get-payment-duration']);
          handleCancel();
          }
        });
    }catch(error: any){
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: 'error'
      });
    }
  }
  
    const formik = useFormik<FormikValues>({
      initialValues: {
        startDate: '',
        endDate: '',
        status: '',
        paymentType: ''
      },
      onSubmit: (value: FormikValues) => {
        state === 'create' ? 
        handlePostPaymentDuration(value) :
        handleEditPaymentDuration(value) 
      },
      validationSchema
    })

      const StatusOptions = (<>
        <option value={'true'}> Active</option>
        <option value = {'false'}>Disabled</option>
      </>)

      const paymentTypeOptions = (<>
        <option value={'Application'}> Application</option>
        <option value = {'Induction'}>Induction</option>
        <option value={'Training'}> Training</option>
        
      </>)

    return(
      <PaymentDurationCreateContainer>
          <FormikProvider value={formik}>
              <form onSubmit={(e) => {e.preventDefault(); formik.submitForm()}}>
                  <FormWrapper>
                    <SubWrapper>
                      <Field 
                          name='startDate'
                          type='date'
                          as={Input}
                          label='Start Date'
                      />
                      <Field 
                          name='endDate'
                          type='date'
                          as={Input}
                          label='End Date'
                      />
                    </SubWrapper>
                    <SubWrapper>
                      <Field 
                          name='status'
                          type='text'
                          as={Select}
                          label='Status'
                          options = {StatusOptions}
                          
                      />
                      <Field 
                          name='paymentType'
                          type='text'
                          as={Select}
                          options = {paymentTypeOptions}
                          label='Payment Type'
                      />
                    </SubWrapper>
                  </FormWrapper>
                  <ButtonWrapper>
                    <ButtonContainer>
                      <span onClick={handleCancel}>Cancel</span>
                      <StyledButton 
                        type="submit"  
                        text={state === 'create' ? 
                          postPaymentDurationMutation.isLoading ? 'loading....' :'Submit'
                          : postPaymentDurationMutation.isLoading ? 'updating....' : 'update'}
                        disabled={postPaymentDurationMutation.isLoading}
                      />
                    </ButtonContainer>
                  </ButtonWrapper>
              </form>
          </FormikProvider>
      </PaymentDurationCreateContainer>
    )
  }


  export default CreatePaymentDuration;
  
  const PaymentDurationCreateContainer = styled('div')`
    margin-inline: 3.5rem;
  `
  const FormWrapper = styled('div')`
     
  `
  
  const ButtonWrapper = styled('div')`
      display: flex;
      justify-content: flex-end;
      margin-block: 2.5rem;

      & span {
        cursor: pointer;
      }
  `
  
  const ButtonContainer = styled('div')`
      display: flex;
      gap: 2rem;
      align-items: center;
  `
  
  const StyledButton = styled(CustomButton)`
      padding-block: 1rem;
      padding-inline: 2rem;
  `
  const SubWrapper = styled('div')`
    display: flex;
    gap: 40px;
    align-items: start;
  `